import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { UsersService } from "../administration/users/users.service";
import { AutoCompleteCompleteEvent } from "primeng/autocomplete";
import { User } from "../administration/users/user.model";
import { MessagesService } from "./messages.service";
import { ConfirmationService, MessageService } from "primeng/api";
import { Chat, ChatMember, ChatMessage } from "./chat.model";
import { SidebarService } from "../shared/sidebar/sidebar.service";
import { AuthService } from "../login/auth.service";
import { ChatRealtimeService } from "./chat-realtime.service";
import { Subscription } from "rxjs";

@Component({
    selector: 'app-messages',
    templateUrl: './messages.component.html',
    styleUrls: ['./messages.component.scss'],
    standalone: false
})
export class MessagesComponent implements OnInit, OnDestroy {
  @ViewChild('messagesEnd') messagesEnd?: ElementRef<HTMLElement>;

  usersService = inject(UsersService);
  private authService = inject(AuthService);
  messagesService = inject(MessagesService);
  messageService = inject(MessageService);
  confirmationService = inject(ConfirmationService);
  sidebarService = inject(SidebarService);
  chatRealtimeService = inject(ChatRealtimeService);

  chats: Chat[] = [];
  selectedChat: Chat | null = null;
  users: User[] = [];
  loading = true;
  sending = false;

  newChatForm = new FormGroup({
    recipients: new FormControl<User[]>([], [Validators.required]),
  });

  replyForm = new FormGroup({
    message: new FormControl<string>('', [Validators.required]),
  });
  private realtimeSubscription?: Subscription;

  async ngOnInit(): Promise<void> {
    if (this.authService.user() === null) {
      await this.authService.fetchMe();
    }
    await this.usersService.fetchUsers();
    this.users = this.availableUsers();
    this.realtimeSubscription = this.chatRealtimeService.messageReceived.subscribe(() => {
      void this.loadChats();
    });
    await this.loadChats();
  }

  ngOnDestroy(): void {
    this.realtimeSubscription?.unsubscribe();
  }

  async loadChats(): Promise<void> {
    this.loading = true;
    try {
      const selectedChatId = this.selectedChat?.id;
      this.chats = this.sortChats(await this.messagesService.fetchMyChats());
      if (selectedChatId) {
        this.selectedChat = this.chats.find((chat) => chat.id === selectedChatId) ?? null;
        if (this.selectedChat) {
          await this.markSelectedChatAsRead();
          this.scrollToLatestMessage();
        }
      } else {
        this.selectedChat = null;
      }
    } catch (error) {
      console.error(error);
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Chats konnten nicht geladen werden.'
      });
    } finally {
      this.loading = false;
    }
  }

  async createChat(): Promise<void> {
    if (this.newChatForm.invalid) return;

    this.sending = true;
    try {
      const { recipients } = this.newChatForm.getRawValue();
      const chat = await this.messagesService.createChat(recipients ?? []);
      this.newChatForm.reset({ recipients: [] });
      await this.loadChats();
      this.selectedChat = this.chats.find((entry) => entry.id === chat.id) ?? this.selectedChat;
      this.scrollToLatestMessage();
      this.messageService.add({
        severity: 'success',
        summary: 'Chat erstellt'
      });
    } catch (error) {
      console.error(error);
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Chat konnte nicht erstellt werden.'
      });
    } finally {
      this.sending = false;
    }
  }

  async deleteChat(event: Event, chat: Chat): Promise<void> {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Diesen Chat mit allen Nachrichten löschen?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Löschen',
      rejectLabel: 'Abbrechen',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        this.sending = true;
        try {
          await this.messagesService.deleteChat(chat.id);
          if (this.selectedChat?.id === chat.id) {
            this.selectedChat = null;
          }
          await this.loadChats();
          this.messageService.add({
            severity: 'success',
            summary: 'Chat gelöscht'
          });
        } catch (error) {
          console.error(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Chat konnte nicht gelöscht werden.'
          });
        } finally {
          this.sending = false;
        }
      }
    });
  }

  async sendMessage(): Promise<void> {
    if (!this.selectedChat || this.replyForm.invalid) return;

    this.sending = true;
    try {
      const message = this.replyForm.getRawValue().message ?? '';
      await this.messagesService.createChatMessage(this.selectedChat.id, message);
      this.replyForm.reset({ message: '' });
      await this.loadChats();
    } catch (error) {
      console.error(error);
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Nachricht konnte nicht gesendet werden.'
      });
    } finally {
      this.sending = false;
    }
  }

  handleReplyKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    void this.sendMessage();
  }

  async selectChat(chat: Chat): Promise<void> {
    this.selectedChat = chat;
    await this.markSelectedChatAsRead();
    this.scrollToLatestMessage();
  }

  closeChatOnMobile(): void {
    this.selectedChat = null;
  }

  filterUsers(event: AutoCompleteCompleteEvent): void {
    const query = event.query.trim().toLowerCase();
    this.users = this.availableUsers().filter((user) =>
      user.first_name?.trim().toLowerCase().includes(query) ||
      user.last_name?.trim().toLowerCase().includes(query) ||
      user.username.trim().toLowerCase().includes(query));
  }

  chatTitle(chat: Chat | null): string {
    if (!chat) return 'Kein Chat ausgewählt';
    if (chat.name) return chat.name;

    const otherMembers = this.otherMembers(chat);
    if (otherMembers.length === 0) return 'Nur du';
    if (chat.type === 'group') {
      return otherMembers.map((member) => this.userName(member.user)).join(', ');
    }

    return this.userName(otherMembers[0].user);
  }

  chatSubtitle(chat: Chat | null): string {
    if (!chat) return '';
    const memberCount = chat.chat_members?.length ?? 0;
    if (chat.type === 'group') return `${memberCount} Personen`;

    return this.otherMembers(chat)
      .map((member) => this.userName(member.user))
      .join(', ');
  }

  otherMembers(chat: Chat): ChatMember[] {
    const currentUserId = this.authService.user()?.id;
    return chat.chat_members?.filter((member) => member.user?.id !== currentUserId) ?? [];
  }

  firstOtherUser(chat: Chat): User | undefined {
    return this.otherMembers(chat)[0]?.user;
  }

  sortedMessages(chat: Chat | null): ChatMessage[] {
    return [...(chat?.chat_messages ?? [])].sort((a, b) => this.messageTime(a) - this.messageTime(b));
  }

  lastMessage(chat: Chat): ChatMessage | undefined {
    const messages = this.sortedMessages(chat);
    return messages[messages.length - 1];
  }

  lastMessagePreview(chat: Chat): string {
    const lastMessage = this.lastMessage(chat);
    if (!lastMessage) return 'Noch keine Nachrichten';

    const prefix = this.isOwnMessage(lastMessage) ? 'Du: ' : '';
    return `${prefix}${lastMessage.body ?? ''}`;
  }

  messageDate(message?: ChatMessage): string | null {
    return message?.sent_at ?? message?.createdAt ?? null;
  }

  showDateSeparator(message: ChatMessage, chat: Chat, index: number): boolean {
    if (index === 0) return true;

    const previousMessage = this.sortedMessages(chat)[index - 1];
    return this.messageDayKey(message) !== this.messageDayKey(previousMessage);
  }

  messageReadState(message: ChatMessage, chat: Chat): 'sent' | 'read' | null {
    if (!this.isOwnMessage(message)) return null;

    const recipients = this.otherMembers(chat);
    if (recipients.length === 0) return null;

    const messageTime = this.messageTime(message);
    const readCount = recipients.filter((member) =>
      member.last_read_at != null && new Date(member.last_read_at).getTime() >= messageTime
    ).length;

    return readCount > 0 ? 'read' : 'sent';
  }

  unreadCount(chat: Chat): number {
    const ownMember = this.ownMember(chat);
    const lastReadAt = ownMember?.last_read_at ? new Date(ownMember.last_read_at).getTime() : 0;
    return (chat.chat_messages ?? []).filter((message) =>
      !this.isOwnMessage(message) && this.messageTime(message) > lastReadAt
    ).length;
  }

  isOwnMessage(message: ChatMessage): boolean {
    return message.users_permissions_user?.id === this.authService.user()?.id;
  }

  userName(user?: User): string {
    if (!user) return 'Unbekannt';
    const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
    return fullName.length > 0 ? fullName : user.username;
  }

  async markSelectedChatAsRead(): Promise<void> {
    if (!this.selectedChat) return;
    await this.messagesService.markChatAsRead(this.selectedChat.id);
    const ownMember = this.ownMember(this.selectedChat);
    if (ownMember) {
      ownMember.last_read_at = new Date().toISOString();
    }
    await this.sidebarService.updateSidebarStats();
  }

  private availableUsers(): User[] {
    const currentUserId = this.authService.user()?.id;
    return this.usersService.users.filter((user) => user.id !== currentUserId);
  }

  private ownMember(chat: Chat): ChatMember | undefined {
    const currentUserId = this.authService.user()?.id;
    return chat.chat_members?.find((member) => member.user?.id === currentUserId);
  }

  private sortChats(chats: Chat[]): Chat[] {
    return [...chats].sort((a, b) => this.chatTime(b) - this.chatTime(a));
  }

  private chatTime(chat: Chat): number {
    return this.messageTime(this.lastMessage(chat)) || new Date(chat.updatedAt ?? chat.createdAt ?? 0).getTime();
  }

  private messageTime(message?: ChatMessage): number {
    if (!message) return 0;
    return new Date(message.sent_at ?? message.createdAt ?? 0).getTime();
  }

  private messageDayKey(message?: ChatMessage): string {
    const date = this.messageDate(message);
    return date ? new Date(date).toDateString() : '';
  }

  private scrollToLatestMessage(): void {
    setTimeout(() => {
      this.messagesEnd?.nativeElement.scrollIntoView({ block: 'end' });
    });
  }
}
